/**
 * Removes layout-list row variant templates that are not wired to a connected
 * `createRowVariant` action on the list behavior graph.
 */

import type {
  ComponentNode,
  ContainerNode,
  LayoutDocument,
  LayoutListComponentProps,
  LayoutNode,
} from "./types.js";

type LayoutListBehavior = NonNullable<LayoutNode["behavior"]>;

/**
 * Returns row variant names declared by connected `createRowVariant` actions.
 *
 * A `createRowVariant` node only counts when its `condition` input is connected,
 * matching the editor dropdown logic in `ListFields`.
 *
 * @param behavior - Behavior graph attached to a `layoutList` component node.
 * @returns Unique, trimmed variant names in discovery order.
 */
export function getLayoutListConnectedRowVariantNames(
  behavior: LayoutListBehavior | undefined,
): string[] {
  if (!behavior?.nodes?.length) {
    return [];
  }

  const connectedConditionNodeIds = new Set(
    behavior.connections
      .filter((connection) => connection.toPortId === "condition")
      .map((connection) => connection.toNodeId),
  );

  const uniqueNames = new Set<string>();
  const names: string[] = [];

  for (const behaviorNode of behavior.nodes) {
    if (behaviorNode.type !== "action") {
      continue;
    }
    if (!("kind" in behaviorNode) || behaviorNode.kind !== "createRowVariant") {
      continue;
    }
    if (!connectedConditionNodeIds.has(behaviorNode.id)) {
      continue;
    }
    const rawName = behaviorNode.ports?.find(
      (port) => port.id === "name",
    )?.value;
    const name = String(rawName ?? "").trim();
    if (!name || uniqueNames.has(name)) {
      continue;
    }
    uniqueNames.add(name);
    names.push(name);
  }

  return names;
}

const pruneContainerNode = (
  container: ContainerNode,
): { node: ContainerNode; changed: boolean } => {
  const childResult = pruneLayoutNodes(container.children);
  if (!childResult.changed) {
    return { node: container, changed: false };
  }
  return {
    node: { ...container, children: childResult.nodes },
    changed: true,
  };
};

const pruneLayoutListComponentNode = (
  node: ComponentNode,
): { node: ComponentNode; changed: boolean } => {
  if (node.component !== "layoutList") {
    return { node, changed: false };
  }

  const props = node.props as LayoutListComponentProps;
  const connectedNames = new Set(
    getLayoutListConnectedRowVariantNames(node.behavior),
  );

  let changed = false;
  let rowTemplate = props.rowTemplate;
  const rowTemplateResult = pruneContainerNode(props.rowTemplate);
  if (rowTemplateResult.changed) {
    rowTemplate = rowTemplateResult.node;
    changed = true;
  }

  const currentVariants = props.rowVariants ?? [];
  const nextVariants = currentVariants.filter((variant) =>
    connectedNames.has(variant.name.trim()),
  );
  if (nextVariants.length !== currentVariants.length) {
    changed = true;
  }

  const prunedVariants = nextVariants.map((variant) => {
    const templateResult = pruneContainerNode(variant.template);
    if (!templateResult.changed) {
      return variant;
    }
    changed = true;
    return { ...variant, template: templateResult.node };
  });

  let editRowVariantName = props.editRowVariantName;
  if (editRowVariantName && !connectedNames.has(editRowVariantName.trim())) {
    editRowVariantName = undefined;
    changed = true;
  }

  if (!changed) {
    return { node, changed: false };
  }

  return {
    node: {
      ...node,
      props: {
        ...props,
        rowTemplate,
        rowVariants: prunedVariants,
        editRowVariantName,
      },
    },
    changed: true,
  };
};

const pruneLayoutNode = (
  node: LayoutNode,
): { node: LayoutNode; changed: boolean } => {
  if (node.kind === "component") {
    return pruneLayoutListComponentNode(node);
  }
  if (node.kind === "container") {
    return pruneContainerNode(node);
  }
  return { node, changed: false };
};

const pruneLayoutNodes = (
  nodes: LayoutNode[],
): { nodes: LayoutNode[]; changed: boolean } => {
  let changed = false;
  const nextNodes = nodes.map((node) => {
    const result = pruneLayoutNode(node);
    if (result.changed) {
      changed = true;
    }
    return result.node;
  });
  return { nodes: nextNodes, changed };
};

/**
 * Returns a layout document ready for persistence by dropping unused
 * `layoutList` row variant templates.
 *
 * @param doc - Editor layout document.
 * @returns The same reference when nothing was removed, otherwise a cloned document.
 */
export function prepareLayoutDocumentForPersistence(
  doc: LayoutDocument,
): LayoutDocument {
  const childResult = pruneLayoutNodes(doc.scene.children);
  if (!childResult.changed) {
    return doc;
  }
  return {
    ...doc,
    scene: {
      ...doc.scene,
      children: childResult.nodes,
    },
  };
}
